<template>
  <VueFinalModal
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-4xl h-[85vh] bg-[#fafbfc] rounded-[40px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
  >
    <div class="flex w-full h-full min-h-0">
      <EditBookSidebar
        :background-image-url="sidebarBackgroundUrl"
        :cover-image-url="sidebarCoverUrl"
        :context-badge-text="'Edit'"
        :context-title="form.title || 'Edit Page'"
        context-subtitle="Update title and cover image"
        :stats-items="[]"
        :footer-value="'Required: title'"
      />

      <div class="flex-1 flex flex-col bg-[#fafbfc] relative overflow-hidden min-w-0">
        <header class="px-8 py-6 border-b border-[#1c4848]/5 flex items-center justify-between bg-white/50 backdrop-blur-sm sticky top-0 z-30">
          <div>
            <div class="flex items-center gap-2 text-[#1c4848]/40 mb-1">
              <i class="pr pr-note-2 text-sm block"></i>
              <span class="text-[10px] font-bold uppercase tracking-wider">/ Pages / Edit</span>
            </div>
            <h2 class="text-xl font-bold text-[#1c4848] tracking-tight">Edit Page</h2>
          </div>
          <div class="flex gap-3">
            <button
              type="button"
              @click="close"
              class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              @click="save"
              :disabled="!canSave || saving"
              class="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#1c4848] shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#1c4848]"
            >
              <i class="pr pr-check block"></i>
              Save changes
            </button>
          </div>
        </header>

        <div class="flex-1 overflow-y-auto p-8 custom-scrollbar">
          <form @submit.prevent="save" class="space-y-8 max-w-2xl mx-auto">
            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-note-2 text-lg block"></i>
                General
              </h4>
              <div class="bg-white rounded-3xl p-6 border border-[#1c4848]/5 shadow-sm">
                <TextInput
                  v-model="form.title"
                  label="Page title"
                  placeholder="Unesite naslov strane"
                  input-id="edit-page-title"
                  right-icon-class="pr pr-edit"
                />
              </div>
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-gallery text-lg block"></i>
                Cover image
              </h4>
              <ImageDropzone
                v-model="form.coverImageFile"
                input-id="edit-page-cover"
                :initial-preview-url="existingCoverUrl"
                title-text="Click or drop to upload cover"
                hint-text="SVG, PNG, JPG or GIF (max. 2MB)"
              />
            </section>
          </form>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { VueFinalModal } from 'vue-final-modal';
import { usePagesStore } from '@/store/pages/index.js';
import EditBookSidebar from '@/components/book-modal/EditBookSidebar.vue';
import TextInput from '@/components/forms/text/TextInput.vue';
import ImageDropzone from '@/components/forms/dropzone/ImageDropzone.vue';

const props = defineProps({
  page: { type: Object, required: true },
});

const emit = defineEmits(['confirm', 'updated']);

const route = useRoute();
const pagesStore = usePagesStore();

const defaultBgUrl = 'https://fra1.digitaloceanspaces.com/aplusspace/69/12c93ede1d5bbc246b9022e680238d18c1d0c2ec.png';

const saving = ref(false);

const form = ref({
  title: props.page?.title ?? props.page?.name ?? '',
  coverImageFile: null,
});

const bookId = computed(() => route.params.bookId);

const existingCoverUrl = computed(() => {
  const p = props.page;
  const media = p?.coverImage ?? p?.cover_image ?? p?.media;
  if (media?.url) return media.url;
  if (typeof p?.cover_image === 'string') return p.cover_image;
  if (typeof p?.image === 'string') return p.image;
  return '';
});

const sidebarBackgroundUrl = computed(() => defaultBgUrl);
const sidebarCoverUrl = computed(() => {
  const f = form.value.coverImageFile;
  if (f instanceof File) return URL.createObjectURL(f);
  return existingCoverUrl.value || '';
});

const canSave = computed(() => String(form.value.title || '').trim().length > 0);

function close() {
  emit('confirm');
}

async function save() {
  if (!canSave.value || saving.value) return;
  const id = props.page?.id;
  if (!id) return;

  saving.value = true;
  try {
    const formData = new FormData();
    formData.append('title', String(form.value.title).trim());
    if (form.value.coverImageFile instanceof File) formData.append('image', form.value.coverImageFile);
    if (bookId.value) formData.append('book_id', String(bookId.value));
    await pagesStore.updatePage(id, formData);
    emit('updated');
    close();
  } catch (err) {
    console.error(err);
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(28, 72, 72, 0.2);
  border-radius: 3px;
}
</style>

